import moment from 'moment';
import { chipColorFor } from '@common/utils/calendar-palette';
import { sendKindOf, type GlyphKind } from '@common/components/kind-glyphs/send-kind';
import type { CalendarLeanEvent } from '../types';
import { occurrenceKey } from './occurrence-key';
import { outcomeOfStatus } from '@common/services/planner-filters';
import type { EffectiveMessageStatus } from '@common-components/message-status/message-status-config';

/** 'status' is a scheduled WhatsApp Status post (the 24-hour story), not a
 *  message to a person. Decided by `sendKindOf`, which the sidebar row uses too. */
export type ChipKind = GlyphKind;
export type ChipStatus = 'sent' | 'unconfirmed' | 'pending' | 'failed';

export interface ChipViewModel {
    key: string;
    kind: ChipKind;
    /** Identity colour: per drip / per campaign / per message. Never status. */
    railColor: string;
    time: string;
    title: string;
    mediaUrl?: string;
    status: ChipStatus;
    isRecurring: boolean;
    isPaused: boolean;
    /** "2/4" for a step of a multi-message campaign, so several chips on
     *  different days read as one sequence instead of unrelated sends. */
    stepLabel?: string;
    /**
     * For a campaign that did not reach everyone: how many went out of how many
     * (`of`), or — when the document does not say how many it queued — how many
     * failed (`failed`). Absent on a fully-sent chip: its green mark already says
     * "all of them", and a "3/3" beside it would only repeat that.
     */
    split?: { kind: 'of'; sent: number; total: number } | { kind: 'failed'; count: number };
}


/**
 * 'unconfirmed' is NOT a failure. getEffectiveMessageStatus is explicit about
 * it: the message was handed to WhatsApp and no delivery confirmation came
 * back — which for a group send is routine and can last weeks. Painting it red
 * is the false alarm that status config goes out of its way to avoid, so it
 * gets its own quiet state: it went out, we just cannot promise it arrived.
 */
function statusOf(event: CalendarLeanEvent): ChipStatus {
    const status = event.effectiveStatus as EffectiveMessageStatus | undefined;
    if (!status) return 'pending';
    if (status === 'unconfirmed') return 'unconfirmed';
    // The chip answers from the SAME status → outcome map the filters use. It used to keep
    // its own two lists, so a raw `error` (a failure) drew as an upcoming chip and a
    // `delivered` send drew as pending: a chip the filters called failed or sent, saying
    // something else. One map, so the chip, the toggle that reveals it and the count agree.
    const outcome = outcomeOfStatus(status);
    if (outcome === 'failed') return 'failed';
    if (outcome === 'sent') return 'sent';
    return 'pending';
}

/** A campaign chip's "how many went out" — only when something did not. */
function splitOf(event: CalendarLeanEvent): ChipViewModel['split'] {
    if (!event.isCampaign) return undefined;
    const s = event.recipientSplit;
    if (s && s.sent < s.total) return { kind: 'of', sent: s.sent, total: s.total };
    if (!s && event.failedCount && event.failedCount > 0) return { kind: 'failed', count: event.failedCount };
    return undefined;
}

function railOf(event: CalendarLeanEvent): string {
    // One curated palette for everything on the grid. Campaigns arrive from the
    // lean selector with a single constant colour (so they all look the same),
    // and messages arrive hashed across 30 saturated hues (so a month looks like
    // confetti). Both go through the same calm set here: a drip's steps share a
    // colour, a standalone campaign gets its own, a message gets its own.
    return chipColorFor(event.isCampaign ? (event.dripId ?? event._id) : event._id);
}

/**
 * Everything a chip needs to render, computed once per event.
 *
 * The rule the current chip gets wrong: the colour rail is IDENTITY (which
 * campaign / drip / message this is), and the dot is STATE. Today the rail is a
 * hash with no meaning, so a red chip says nothing — here red always means the
 * send failed.
 */
export function buildChipViewModel(
    event: CalendarLeanEvent,
    _now: number,
    stepCount?: number,
): ChipViewModel {
    const kind = sendKindOf(event);
    const stepLabel = event.dripId && typeof event.dripStepIndex === 'number' && (stepCount ?? 0) > 1
        ? `${event.dripStepIndex + 1}/${stepCount}`
        : undefined;
    return {
        key: occurrenceKey(event),
        kind,
        railColor: railOf(event),
        time: moment(event.dueDate).format('HH:mm'),
        title: (event.isPoll ? event.poll?.question : event.message) || event.message || '',
        mediaUrl: event.asset?.url ?? undefined,
        status: statusOf(event),
        isRecurring: !!event.isRecurring,
        isPaused: event.campaignStatus === 'paused',
        stepLabel,
        split: splitOf(event),
    };
}
